import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { ShareReportModal } from '@/components/share-landing/ShareReportModal';
import {
  SharePageLayout,
  ShareAcceptCard,
  ShareSuccessCard,
  ShareErrorCard,
  ShareDeclinedCard,
  ShareLoadingCard,
  ShareOwnerCard,
} from '@/components/share-landing';
import {
  publicGetShareLanding,
  publicGetShareLandingAuthenticated,
  publicConfirmCopyBySession,
  trackShareEvent
} from '@/services/shareVideoService';

type LandingState =
  | 'loading'         // Carregando dados públicos
  | 'accept'          // Mostrando card de aceite (público - sem exigir login)
  | 'processing'      // Processando cópia (requer login)
  | 'success'         // Cópia realizada
  | 'error'           // Token inválido/expirado
  | 'owner_blocked'   // Usuário é dono do vídeo (só após login)
  | 'declined';       // Usuário recusou

type ErrorType = 'not_found' | 'revoked' | 'expired' | 'limit_reached' | 'already_cloned' | 'generic';

export default function ShareLanding() {
  useLanguage();
  const { shareCode } = useParams<{ shareCode: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();

  const [state, setState] = useState<LandingState>('loading');
  const [errorType, setErrorType] = useState<ErrorType>('generic');
  const [showReportModal, setShowReportModal] = useState(false);
  const [copiedVideoTitle, setCopiedVideoTitle] = useState('');
  const [pendingAutoAccept, setPendingAutoAccept] = useState(false);

  // ========== STEP 1: Load PUBLIC data first (no login required) ==========
  const { data: publicData, isLoading: publicLoading, error: publicError } = useQuery({
    queryKey: ['share-landing-public', shareCode],
    queryFn: () => publicGetShareLanding(shareCode!),
    enabled: !!shareCode
  });

  // ========== STEP 2: Load authenticated data (only if logged in and state is accept) ==========
  const { data: authData } = useQuery({
    queryKey: ['share-landing-auth', shareCode, user?.id_user],
    queryFn: () => publicGetShareLandingAuthenticated(shareCode!, user!.id_user),
    enabled: !!shareCode && !!user && state === 'accept'
  });

  // ========== STEP 3: Mutation to confirm copy ==========
  const copyMutation = useMutation({
    mutationFn: () => publicConfirmCopyBySession(shareCode!, user!.id_user),
    onMutate: () => setState('processing'),
    onSuccess: (res) => {
      if (res.status === 'success') {
        trackShareEvent('share_copy_succeeded', { shareCode, userId: user?.id_user });
        setCopiedVideoTitle(publicData?.videoPreview?.title || 'Vídeo');
        setState('success');
      } else {
        trackShareEvent('share_copy_failed', { shareCode, userId: user?.id_user, error: res.error });
        setErrorType(res.code === 'VIDEO_ALREADY_CLONED' ? 'already_cloned' : 'generic');
        setState('error');
      }
    },
    onError: () => {
      setErrorType('generic');
      setState('error');
    }
  });

  // ========== Process public data and set initial state ==========
  useEffect(() => {
    if (publicLoading || !publicData) return;

    // Track view for active links
    if (publicData.status === 'active') {
      trackShareEvent('share_link_viewed', { shareCode, userId: user?.id_user || 'anonymous' });
    }

    // Set state based on public data
    switch (publicData.status) {
      case 'active':
        setState('accept');
        break;
      case 'not_found':
        setErrorType('not_found');
        setState('error');
        break;
      case 'revoked':
        setErrorType('revoked');
        setState('error');
        break;
      case 'expired':
        setErrorType('expired');
        setState('error');
        break;
      case 'limit_reached':
        setErrorType('limit_reached');
        setState('error');
        break;
      default:
        setErrorType('generic');
        setState('error');
    }
  }, [publicData, publicLoading, shareCode, user?.id_user]);

  // ========== Handle public data error ==========
  useEffect(() => {
    if (publicError) {
      setErrorType('generic');
      setState('error');
    }
  }, [publicError]);

  // ========== Check if user is owner (only after login) ==========
  useEffect(() => {
    if (authData?.isOwner && state === 'accept') {
      setState('owner_blocked');
    }
  }, [authData, state]);

  // ========== Auto-accept after returning from login ==========
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get('action');

    // Check if we returned from login with action=accept
    if (action === 'accept' && state === 'accept') {
      setPendingAutoAccept(true);
      // Clean URL parameter
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, state, navigate, location.pathname]);

  // ========== Execute auto-accept when user is logged in and pending ==========
  useEffect(() => {
    if (pendingAutoAccept && user && !authLoading && state === 'accept') {
      // Check if owner first (wait for authData)
      if (authData?.isOwner) {
        setState('owner_blocked');
        setPendingAutoAccept(false);
        return;
      }

      // If authData loaded and not owner, proceed with copy
      if (authData && !authData.isOwner) {
        setPendingAutoAccept(false);
        copyMutation.mutate();
      }
    }
  }, [pendingAutoAccept, user, authLoading, state, authData, copyMutation]);

  // ========== Handlers ==========
  const handleAccept = () => {
    // Only check for login when user clicks "Accept"
    if (!user) {
      // Redirect to login passing return URL + action via router state
      navigate('/login', {
        replace: true,
        state: { from: { pathname: `${location.pathname}?action=accept` } }
      });
      return;
    }

    // User is logged in → check if owner
    if (authData?.isOwner) {
      setState('owner_blocked');
      return;
    }

    // User is logged in and not owner → process copy
    copyMutation.mutate();
  };

  const handleDecline = () => {
    setState('declined');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoToVideos = () => {
    navigate('/');
  };

  const handleGoToShared = () => {
    navigate('/distribuir');
  };

  // ========== Render based on state ==========
  const renderContent = () => {
    // Loading state (loading public data)
    if (state === 'loading' || publicLoading) {
      return <ShareLoadingCard />;
    }

    // Accept card (visible to everyone, logged in or not)
    if (state === 'accept' || state === 'processing') {
      return (
        <ShareAcceptCard
          videoInfo={{
            title: publicData?.videoPreview?.title || translateInterfaceText("Vídeo de vendas"),
            ownerName: publicData?.ownerName || '',
            ownerId: publicData?.ownerId || ''
          }}
          isProcessing={state === 'processing'}
          onAccept={handleAccept}
          onDecline={handleDecline}
        />
      );
    }

    // Success card
    if (state === 'success') {
      return (
        <ShareSuccessCard
          videoTitle={copiedVideoTitle}
          onGoToVideos={handleGoToVideos}
          onClose={handleGoHome}
        />
      );
    }

    // Error card
    if (state === 'error') {
      return (
        <ShareErrorCard
          errorType={errorType}
          onGoHome={handleGoHome}
        />
      );
    }

    // Declined card
    if (state === 'declined') {
      return (
        <ShareDeclinedCard
          onGoHome={handleGoHome}
        />
      );
    }

    // Owner blocked
    if (state === 'owner_blocked') {
      return (
        <ShareOwnerCard
          videoTitle={publicData?.videoPreview?.title || 'Vídeo'}
          onGoToShared={handleGoToShared}
        />
      );
    }

    return null;
  };

  return (
    <SharePageLayout>
      {renderContent()}

      {/* Report Modal (overlay) */}
      <ShareReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        shareCode={shareCode || ''}
      />
    </SharePageLayout>
  );
}
